// Settings panel — surfaces the Twilio operational state inside the
// CRM so the operator never has to scrape Twilio Console manually.
// Reads /api/twilio/health (which both this app and sfa-admin can
// hit). Auto-hides for non-admin users since the endpoint will 401.
// Warnings render as red banners; healthy state shows a green chip
// per pillar (brand / balance / SMS gate).

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { supabase } from '../lib/supabase';

async function apiFetch(path) {
  const jwt = readPersistedJwtDirect();
  if (!jwt) throw new Error('Not signed in');
  const res = await fetch(path, { headers: { Authorization: `Bearer ${jwt}` } });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(body?.error || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return body;
}

function StatusPill({ tone, label }) {
  const palette = {
    green:  'bg-emerald-100 text-emerald-800 border-emerald-200',
    amber:  'bg-amber-100  text-amber-800   border-amber-200',
    red:    'bg-red-100    text-red-800     border-red-200',
    slate:  'bg-slate-100  text-slate-700   border-slate-200',
  };
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${palette[tone] || palette.slate}`}>{label}</span>;
}

function brandTone(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'APPROVED')                      return 'green';
  if (s === 'FAILED' || s === 'SUSPENDED')   return 'red';
  if (s === 'PENDING' || s === 'IN_REVIEW')  return 'amber';
  return 'slate';
}

export default function TwilioHealthPanel() {
  const [data, setData]       = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr]         = React.useState(null);
  const [refreshTick, setRefreshTick] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr(null);
    (async () => {
      try {
        const j = await apiFetch('/api/twilio/health');
        if (!cancelled) setData(j);
      } catch (e) {
        if (e.status === 401) { setErr(null); setData(null); } // not an admin → hide silently
        else if (!cancelled)  { setErr(e.message || String(e)); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshTick]);

  if (loading) return null;
  if (err)     return null;
  if (!data)   return null;

  const bal = data.balance?.balance_float;
  const balTone = bal == null ? 'slate' : (bal < 50 ? 'red' : bal < 200 ? 'amber' : 'green');
  const smsTone = data.sms_send_enabled ? 'green' : 'amber';
  const pbTone  = brandTone(data.parent_brand?.status);
  const sum     = data.tenant_a2p_summary || {};

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
        <h3 className="font-bold">Relay operational health</h3>
        <button
          onClick={() => setRefreshTick(n => n + 1)}
          className="text-xs text-emerald-700 hover:text-emerald-900 font-semibold"
        >
          Refresh →
        </button>
      </div>

      {/* Warnings — red banners for active issues. */}
      {Array.isArray(data.warnings) && data.warnings.length > 0 && (
        <div className="mb-4 space-y-1.5">
          {data.warnings.map((w, i) => (
            <div key={i} className="text-xs bg-red-50 border border-red-200 text-red-900 rounded-lg px-3 py-2 leading-relaxed">
              ⚠ {w}
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Parent brand */}
        <div className="border border-slate-200 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Parent A2P brand</div>
          <div className="flex items-center gap-2 flex-wrap">
            <StatusPill tone={pbTone} label={data.parent_brand?.status || 'unknown'}/>
            {data.parent_brand?.type && <span className="text-[10px] text-slate-500">{data.parent_brand.type}</span>}
          </div>
          {data.parent_brand?.name && <div className="text-sm font-semibold text-slate-900 mt-1.5 truncate">{data.parent_brand.name}</div>}
          {data.parent_brand?.last_error && (
            <div className="text-[11px] text-red-700 mt-1.5 leading-snug">{data.parent_brand.last_error}</div>
          )}
        </div>

        {/* Balance */}
        <div className="border border-slate-200 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Account balance</div>
          <div className="flex items-center gap-2">
            <StatusPill tone={balTone} label={bal != null ? `$${bal.toFixed(2)}` : 'unknown'}/>
            {data.balance?.account_type && <span className="text-[10px] text-slate-500">{data.balance.account_type}</span>}
          </div>
          <div className="text-[11px] text-slate-500 mt-1.5 leading-snug">
            ~$280 one-time + ~$23/mo to onboard 20 sub-tenants.
          </div>
        </div>

        {/* SMS_SEND_ENABLED */}
        <div className="border border-slate-200 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">SMS send gate</div>
          <StatusPill tone={smsTone} label={data.sms_send_enabled ? 'Live' : 'Dry-run'}/>
          <div className="text-[11px] text-slate-500 mt-1.5 leading-snug">
            {data.sms_send_enabled
              ? 'Live — outbound SMS reaches phones.'
              : 'Off — every send logs as a dry-run. Flip SMS_SEND_ENABLED=true on Vercel after A2P approval.'}
          </div>
        </div>
      </div>

      {/* Fleet A2P summary */}
      <div className="mt-4 border-t border-slate-100 pt-3">
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">Sub-tenant A2P fleet ({sum.total || 0} active subscriptions)</div>
        <div className="flex items-center gap-2 flex-wrap text-[11px]">
          <span className="bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full px-2 py-0.5">{sum.approved || 0} approved</span>
          <span className="bg-amber-50   text-amber-800   border border-amber-200   rounded-full px-2 py-0.5">{sum.in_review || 0} in review</span>
          <span className="bg-amber-50   text-amber-800   border border-amber-200   rounded-full px-2 py-0.5">{sum.pending || 0} pending</span>
          <span className="bg-red-50     text-red-800     border border-red-200     rounded-full px-2 py-0.5">{sum.failed || 0} failed</span>
          <span className="bg-slate-50   text-slate-700   border border-slate-200   rounded-full px-2 py-0.5">{sum.no_brand || 0} no brand</span>
        </div>
        {data.last_sweep?.most_recent_update && (
          <div className="text-[11px] text-slate-500 mt-2">
            Most recent a2p-sweep update: {new Date(data.last_sweep.most_recent_update).toLocaleString()}.
          </div>
        )}
      </div>
    </div>
  );
}
